import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { gccFlowsService } from '../../services/gccFlowsService';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import PlotlyChart from '../../components/charts/PlotlyChart';
import { HEATMAP_RETURNS } from '../../components/charts/chartTheme';
import CollapsibleSection from '../signals/components/CollapsibleSection';

const PERIODS = [
  { label: '1D', value: '1d' },
  { label: 'WTD', value: 'wtd' },
  { label: 'MTD', value: 'mtd' },
  { label: 'YTD', value: 'ytd' },
];
const METRICS = [
  { label: '$ mn', value: 'usd' },
  { label: '% of Mcap (YTD only)', value: 'pct_mcap' },
];

const fmt = (v, dp = 2) => {
  if (v == null || isNaN(v)) return '-';
  return Number(v).toLocaleString(undefined, { maximumFractionDigits: dp });
};
const signClass = (n) => (n == null ? '' : Number(n) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400');

const SectorFlowsPage = ({ filterParams }) => {
  const [period, setPeriod] = useState('ytd');
  const [metric, setMetric] = useState('usd');
  const [heatmap, setHeatmap] = useState(null);
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [hm, tr] = await Promise.all([
        gccFlowsService.getSectorHeatmap({ period, metric }),
        gccFlowsService.getSectorTrend(filterParams),
      ]);
      setHeatmap(hm.data);
      setTrend(tr.data || []);
    } finally {
      setLoading(false);
    }
  }, [filterParams, period, metric]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Build heatmap z-matrix: sectors (rows) × markets (cols)
  const { z, x, y, hover } = useMemo(() => {
    if (!heatmap) return { z: [], x: [], y: [], hover: [] };
    const sectors = heatmap.sectors || [];
    const markets = heatmap.markets || [];
    const valueByCell = {};
    (heatmap.rows || []).forEach((r) => {
      valueByCell[`${r.market}|${r.sector}`] = r.value;
    });
    const z = sectors.map((s) => markets.map((m) => valueByCell[`${m}|${s}`] ?? null));
    const hover = sectors.map((s) =>
      markets.map((m) => {
        const v = valueByCell[`${m}|${s}`];
        return `<b>${m} — ${s}</b><br>${metric === 'usd' ? '$' : ''}${fmt(v, 2)}${metric === 'pct_mcap' ? '%' : 'mn'}`;
      })
    );
    return { z, x: markets, y: sectors, hover };
  }, [heatmap, metric]);

  // Sector totals across all markets (for the trend chart): aggregate net_usd_1d
  const sectorTrend = useMemo(() => {
    const bySectorDate = {};
    trend.forEach((r) => {
      const key = `${r.sector}|${r.report_date}`;
      bySectorDate[key] = (bySectorDate[key] || 0) + (r.net_usd_1d || 0);
    });
    const sectors = Array.from(new Set(trend.map((r) => r.sector))).sort();
    return sectors.map((s) => {
      const dates = Array.from(new Set(trend.filter((r) => r.sector === s).map((r) => r.report_date))).sort();
      let cum = 0;
      const cumY = dates.map((d) => {
        cum += bySectorDate[`${s}|${d}`] || 0;
        return cum;
      });
      return { name: s, x: dates, y: cumY, type: 'scatter', mode: 'lines' };
    });
  }, [trend]);

  if (loading && !heatmap) return <LoadingSpinner message="Loading sector flows..." />;

  return (
    <div>
      <CollapsibleSection title="Markets × Sectors Heatmap">
        <div className="flex flex-wrap gap-2 mb-3">
          <div className="flex gap-1">
            {PERIODS.map((p) => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                disabled={metric === 'pct_mcap' && p.value !== 'ytd'}
                className={`text-xs px-2.5 py-1 rounded-md disabled:opacity-30 disabled:cursor-not-allowed ${
                  period === p.value
                    ? 'bg-[#c9a96e] text-white'
                    : 'border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex gap-1">
            {METRICS.map((m) => (
              <button
                key={m.value}
                onClick={() => {
                  setMetric(m.value);
                  if (m.value === 'pct_mcap') setPeriod('ytd');
                }}
                className={`text-xs px-2.5 py-1 rounded-md ${
                  metric === m.value
                    ? 'bg-[#c9a96e] text-white'
                    : 'border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400'
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>

        {z.length === 0 ? (
          <div className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">No data</div>
        ) : (
          <PlotlyChart
            interactive
            data={[{
              z,
              x,
              y,
              type: 'heatmap',
              colorscale: HEATMAP_RETURNS,
              zmid: 0,
              hoverinfo: 'text',
              text: hover,
              colorbar: { title: metric === 'usd' ? '$ mn' : '%' },
            }]}
            layout={{
              margin: { t: 20, b: 60, l: 160, r: 20 },
              xaxis: { side: 'top' },
              yaxis: { autorange: 'reversed' },
            }}
          />
        )}
      </CollapsibleSection>

      <CollapsibleSection title="Cumulative Sector Flow ($mn) — across selected markets">
        {sectorTrend.length === 0 ? (
          <div className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">No data in selected period.</div>
        ) : (
          <PlotlyChart
            data={sectorTrend}
            layout={{
              showlegend: true,
              legend: { orientation: 'h', y: -0.25 },
              hovermode: 'x unified',
              margin: { t: 20, b: 100, l: 50, r: 20 },
              yaxis: { title: '$ mn cumulative', zeroline: true },
            }}
          />
        )}
      </CollapsibleSection>

      {/* Latest table */}
      {heatmap?.rows?.length > 0 && (
        <CollapsibleSection title={`Latest by Sector (${period.toUpperCase()}, ${metric === 'usd' ? '$ mn' : '% of Mcap'})`} defaultOpen={false}>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 dark:bg-[#0d1117]">
                <tr>
                  <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">Market</th>
                  <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">Sector</th>
                  <th className="px-3 py-2 text-right text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">Value</th>
                  <th className="px-3 py-2 text-right text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">Mcap ($mn)</th>
                  <th className="px-3 py-2 text-right text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">FO % Mcap</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {heatmap.rows.map((r, i) => (
                  <tr key={i}>
                    <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300">{r.market}</td>
                    <td className="px-3 py-1.5 text-gray-800 dark:text-gray-200">{r.sector}</td>
                    <td className={`px-3 py-1.5 text-right tabular-nums font-medium ${signClass(r.value)}`}>
                      {r.value == null ? '-' : `${Number(r.value) >= 0 ? '+' : ''}${fmt(r.value, 2)}${metric === 'pct_mcap' ? '%' : ''}`}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-gray-600 dark:text-gray-400">{fmt(r.mcap_usd, 0)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-gray-600 dark:text-gray-400">{r.fo_pct_mcap == null ? '-' : `${(Number(r.fo_pct_mcap) * 100).toFixed(2)}%`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CollapsibleSection>
      )}
    </div>
  );
};

export default SectorFlowsPage;
